import { cn } from '@/lib/utils';

/**
 * BrixLogo — single source of truth for the Brix Beverage brand mark in the UI.
 *
 * Renders an inline SVG approximation of the round °bx™ mark (navy circle with
 * a glass highlight, white wordmark, optional ™ glyph). The wordmark renders in
 * the native system font stack (no web-font load).
 *
 * **Swap-in for the official asset**:
 *   When the production logo SVG/PNG is dropped at
 *   `public/brand/brix-logo-round.svg`, edit `RoundMark` below to render
 *   `<img src="/brand/brix-logo-round.svg" alt="Brix Beverage" />` instead of
 *   the inline SVG. Every consumer in the app passes through this component,
 *   so the swap is one file.
 *
 * Brand rules honored:
 *  - Navy `#1F4E79` (matches the actual round logo + Operations Hub family).
 *  - Native system font stack (no web-font load).
 *  - No banned colors (#C8102E crimson, AI-purples, neons).
 */

type Variant = 'mark' | 'full';
type Size = 'sm' | 'md' | 'lg' | 'xl';

interface BrixLogoProps {
  variant?: Variant;
  size?: Size;
  /** Use light-on-dark coloring for placement over the Brix-primary header. */
  inverse?: boolean;
  /** Render the "Powered by Alameda Soda Co." tagline under the wordmark. */
  showTagline?: boolean;
  /** Override the wordmark text. Defaults to "Brix Order". */
  label?: string;
  /** Render the ™ glyph below-right of the round mark. */
  showTrademark?: boolean;
  /** Wrap the mark in a soft radial glow — used on dark header chrome. */
  glow?: boolean;
  className?: string;
}

const MARK_SIZE_PX: Record<Size, number> = {
  sm: 28,
  md: 36,
  lg: 56,
  xl: 80,
};

/**
 * Heights (px) of the full PNG logo from /public/brand/brix-logo-full.png.
 * The image is 1280×578 (≈2.21:1) so width auto-scales from the height.
 *
 * `md` is the header default; it was bumped from 40 → 46 (~15%) to satisfy
 * Sky's "main page logo … needs to be 15% larger" ask.
 */
const FULL_LOGO_HEIGHT_PX: Record<Size, number> = {
  sm: 28,
  md: 44,
  lg: 62,
  xl: 93,
};

/**
 * Heights (px) of the Alameda Soda Co. mark at /public/brand/alameda-soda-logo.png.
 * The image is 1920×321 (~6:1) so width auto-scales from the height.
 */
const ALAMEDA_LOGO_HEIGHT_PX: Record<Size, number> = {
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
};

const GAP: Record<Size, string> = {
  sm: 'gap-2',
  md: 'gap-2.5',
  lg: 'gap-3',
  xl: 'gap-3.5',
};

/**
 * The round °bx mark itself. Inline SVG so it scales perfectly and can be
 * styled via CSS variables. Replace the inner SVG with an <img> tag once the
 * official artwork lands in /public/brand/.
 */
function RoundMark({ size, showTrademark }: { size: Size; showTrademark: boolean }) {
  const px = MARK_SIZE_PX[size];

  return (
    <div
      className="relative inline-block"
      style={{ width: px, height: px }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 240 240"
        xmlns="http://www.w3.org/2000/svg"
        width={px}
        height={px}
        className="block"
      >
        <defs>
          <radialGradient id="brixCircle" cx="38%" cy="30%" r="80%">
            <stop offset="0%"  stopColor="#2D5F8E" />
            <stop offset="55%" stopColor="#1F4E79" />
            <stop offset="100%" stopColor="#143358" />
          </radialGradient>
          <linearGradient id="brixSheen" x1="0%" y1="0%" x2="100%" y2="60%">
            <stop offset="0%"  stopColor="#ffffff" stopOpacity="0.45" />
            <stop offset="55%" stopColor="#ffffff" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Solid navy disc with subtle inner gradient for depth */}
        <circle cx="120" cy="120" r="116" fill="url(#brixCircle)" />

        {/* Glass sheen in the upper-right */}
        <ellipse
          cx="158"
          cy="74"
          rx="78"
          ry="42"
          fill="url(#brixSheen)"
          transform="rotate(-32 158 74)"
        />

        {/* Thin inner ring for a polished edge */}
        <circle
          cx="120"
          cy="120"
          r="112"
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="1.5"
        />

        {/* Degree mark — small circle, upper-left of the wordmark area */}
        <circle cx="71" cy="92" r="9" fill="none" stroke="#ffffff" strokeWidth="6" />

        {/* Wordmark — bx, system font, heavy weight, tight tracking */}
        <text
          x="120"
          y="170"
          textAnchor="middle"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"
          fontWeight={800}
          fontSize={132}
          fill="#ffffff"
          letterSpacing="-6"
        >
          bx
        </text>
      </svg>

      {showTrademark && (
        <span
          aria-hidden
          className="absolute -bottom-1 -right-1 text-brix-primary font-display font-semibold"
          style={{ fontSize: Math.max(8, Math.round(px * 0.16)) }}
        >
          ™
        </span>
      )}
    </div>
  );
}

export function BrixLogo({
  variant = 'full',
  size = 'md',
  inverse = false,
  showTagline = true,
  showTrademark = false,
  glow = false,
  label = 'Brix Order',
  className,
}: BrixLogoProps) {
  if (variant === 'mark') {
    return (
      <div className={cn('inline-flex', className)} aria-label="Brix Beverage">
        <RoundMark size={size} showTrademark={showTrademark} />
      </div>
    );
  }

  // Full Brix Beverage brand mark sourced from brixbev.com, served from
  // /public/brand/brix-logo-full.png. The PNG already contains both the round
  // mark and the wordmark.
  //
  // When showTagline is set, render the actual Alameda Soda Co. logo from
  // /public/brand/alameda-soda-logo.png (sourced from alamedasoda.com) instead
  // of a "Powered by" text — true partner attribution.
  return (
    <div className={cn('inline-flex items-center', GAP[size], className)} aria-label={label}>
      <img
        src="/brand/brix-logo-full.png"
        alt={label}
        height={FULL_LOGO_HEIGHT_PX[size]}
        style={{
          height: FULL_LOGO_HEIGHT_PX[size],
          width: 'auto',
          filter: glow
            ? 'drop-shadow(0 0 18px rgba(255,255,255,0.55)) drop-shadow(0 0 6px rgba(255,255,255,0.4))'
            : undefined,
        }}
        className="block select-none"
        draggable={false}
      />
      {showTagline && (
        <div
          className={cn(
            'flex flex-col items-start leading-none',
            inverse ? 'text-white/60' : 'text-brix-muted',
          )}
        >
          <span className="text-[10px] tracking-tight opacity-70">Powered by</span>
          <img
            src="/brand/alameda-soda-logo.png"
            alt="Alameda Soda Co."
            height={ALAMEDA_LOGO_HEIGHT_PX[size]}
            style={{
              height: ALAMEDA_LOGO_HEIGHT_PX[size],
              width: 'auto',
              marginTop: 2,
            }}
            className="block select-none"
            draggable={false}
          />
        </div>
      )}
    </div>
  );
}
